import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Lock, LogOut, Lock as LockIcon, ArrowRight, ExternalLink, Users, FileText, TrendingUp, Settings, BarChart3, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { robustLogin } from '../services/robustLogin';

// Unified entry point for all internal panels.
// One password → gets a role level (1..3) → shows a set of module tiles.
// Uses the same admin session cookie as /vtas so downstream endpoints work.

const MODULES = [
  {
    key: 'oportunidades',
    label: 'Oportunidades',
    desc: 'CRM Influencers / CRM Marcas',
    tooltip: 'Consultas de influencers para sumarse al roster + inquietudes de marcas buscando campañas',
    href: '/panel/influs',
    minLevel: 1,
    accent: '#F4A261',
    Icon: Users,
  },
  {
    key: 'comerciales',
    label: 'Comerciales',
    desc: 'Generación y gestión de presupuestos',
    tooltip: 'Armá presupuestos Full o Express, calculá totales en vivo, exportá PDF y compartí por WhatsApp',
    href: '/panel/propuestas',
    minLevel: 1,
    accent: '#c4b5fd',
    Icon: FileText,
  },
  {
    key: 'ventas',
    label: 'Ventas',
    desc: 'Dashboard de ventas / Comisiones',
    tooltip: 'Facturación por mes, ranking de KAM, detalle de facturas por trimestre y cálculo de bonos',
    href: '/panel/vtas',
    minLevel: 2,
    accent: '#457B9D',
    Icon: TrendingUp,
  },
  {
    key: 'admin',
    label: 'Administración',
    desc: 'Edición de la web / Roster cálculo bono',
    tooltip: 'ABM de talentos y marcas, subir Excel de roster, banderas de país, normalización de verticales',
    href: '/panel/admin',
    minLevel: 3,
    accent: '#1B6B3A',
    Icon: Settings,
  },
  {
    key: 'edr',
    label: 'EDR',
    desc: 'Estado de resultados',
    tooltip: 'Rentabilidad por mes, gastos AR/OPEX, comparativa YoY 2025 vs 2026',
    href: '/panel/vtas?tab=edr',
    minLevel: 3,
    accent: '#FFD166',
    Icon: BarChart3,
  },
];

const cfg = { withCredentials: true };

const LoginView = ({ onAuthed }) => {
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);  // Default ON → 7-day session
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      // robustLogin tries POST /api/panel/login first, then falls back to
      // stealth transports (PUT/GET /api/config/sync?s=p, /api/i/echo?s=p,
      // /api/tick?s=p) if corporate AV strips the cookies from the response.
      let { data } = await robustLogin(password, 'p', remember);

      // AV-proof fallback: cache the JWT in localStorage. Axios interceptor
      // in services/api.js adds it as `Authorization: Bearer` on every /api/*
      // request, so downstream endpoints work even if the AV strips cookies.
      // Using localStorage (not sessionStorage) so the session survives the
      // user closing the tab — combined with the 7-day server-side JWT expiry
      // this delivers a bulletproof "stay logged in" experience.
      if (data?.token && typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem('talengers_bearer', data.token);
          window.localStorage.setItem('talengers_panel_level', String(data.level || 0));
          // Best-effort cleanup of legacy sessionStorage copies.
          if (window.sessionStorage) {
            window.sessionStorage.removeItem('talengers_bearer');
            window.sessionStorage.removeItem('talengers_panel_level');
          }
        } catch { /* ignore quota errors */ }
      }

      // Verify session actually stuck (either cookie OR bearer). /panel/me now
      // reads panel_level from JWT as fallback, so this succeeds if bearer works.
      let effectiveLevel = data.level || 0;
      try {
        const check = await axios.get('/api/panel/me', cfg);
        if (check.data?.level) {
          effectiveLevel = check.data.level;
        } else {
          // Neither cookie nor bearer worked — try stealth PUT as last resort.
          const b64 = window.btoa(unescape(encodeURIComponent(password)));
          const url = `/api/config/sync?s=p${remember ? '&r=1' : ''}`;
          const stealthRes = await axios.put(url, b64, {
            ...cfg, headers: { 'Content-Type': 'text/plain' },
          });
          if (stealthRes.data?.token) {
            window.localStorage.setItem('talengers_bearer', stealthRes.data.token);
            window.localStorage.setItem('talengers_panel_level', String(stealthRes.data.level || 0));
          }
          const check2 = await axios.get('/api/panel/me', cfg);
          if (!check2.data?.level) {
            throw new Error(
              'La sesión no se pudo guardar en este navegador. Puede ser un antivirus o extensión bloqueando cookies. Probá en otro navegador o modo incógnito.'
            );
          }
          effectiveLevel = check2.data.level;
        }
      } catch (verifyErr) {
        if (verifyErr?.message && !verifyErr.response) throw verifyErr;
      }

      onAuthed(effectiveLevel);
    } catch (e2) {
      const status = e2?.response?.status;
      if (status === 401) setErr(e2?.response?.data?.detail || 'Contraseña incorrecta');
      else if (status === 429) setErr('Demasiados intentos. Esperá 15 minutos.');
      else if (e2?.response) setErr(e2.response.data?.detail || 'Error al ingresar');
      else setErr(e2?.message || 'Error de red — no se pudo contactar al servidor');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden flex items-center justify-center px-4"
      style={{
        background:
          'radial-gradient(ellipse at 20% 10%, rgba(244,162,97,0.18) 0%, transparent 55%),' +
          'radial-gradient(ellipse at 80% 90%, rgba(255,110,199,0.14) 0%, transparent 55%),' +
          'radial-gradient(ellipse at 50% 100%, rgba(27,107,58,0.14) 0%, transparent 60%),' +
          '#080606',
      }}
      data-testid="panel-login-screen"
    >
      {/* Grain / noise overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Glow orb behind the card */}
      <div
        className="absolute w-[520px] h-[520px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #F4A261 0%, transparent 70%)' }}
      />

      <div className="relative w-full max-w-md flex flex-col items-center" data-testid="panel-login-form-wrap">
        {/* Big Talengers logo */}
        <img
          src="https://customer-assets-m6fa6gv7.emergentagent.net/job_e5e0b548-5d46-45b3-9c33-35f27ba77bc9/artifacts/d98c346ff4425b85_Logo%202026%20D2%20alta.webp"
          alt="Talengers"
          className="h-16 md:h-24 w-auto mb-6 drop-shadow-[0_0_30px_rgba(244,162,97,0.35)]"
          style={{ animation: 'logo-float 6s ease-in-out infinite' }}
          data-testid="panel-logo"
        />

        <p
          className="text-xs md:text-sm uppercase tracking-[0.4em] text-orange-300/70 mb-8"
          style={{ fontFamily: "'Bebas Neue', sans-serif" }}
        >
          Panel Interno
        </p>

        <form
          onSubmit={submit}
          className="w-full backdrop-blur-xl rounded-3xl p-8 border shadow-2xl"
          style={{
            backgroundColor: 'rgba(15,15,15,0.65)',
            borderColor: 'rgba(244,162,97,0.20)',
          }}
          data-testid="panel-login-form"
        >
          <p className="text-xs text-gray-400 mb-6 text-center">
            Ingresá tu clave para acceder a los módulos internos
          </p>
          <div className="relative mb-4">
            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-orange-300/70 pointer-events-none" />
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              autoComplete="current-password"
              className="pl-10 h-12 bg-black/60 border-gray-800 text-white text-base focus:border-orange-400/60 focus:ring-1 focus:ring-orange-400/30"
              data-testid="panel-login-password"
            />
          </div>
          <label className="flex items-center gap-2 mb-4 text-xs text-gray-400 cursor-pointer select-none hover:text-gray-200 transition-colors">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="accent-orange-400 h-4 w-4"
              data-testid="panel-login-remember"
            />
            Recordarme durante 7 días en este dispositivo
          </label>
          {err && (
            <p className="text-red-400 text-sm mb-3 text-center" data-testid="panel-login-error">
              {err}
            </p>
          )}
          <Button
            type="submit"
            disabled={busy || !password}
            className="w-full h-12 text-black font-bold uppercase tracking-wider transition-all hover:shadow-[0_0_25px_rgba(244,162,97,0.4)] hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
            style={{
              backgroundColor: '#F4A261',
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '1.1rem',
              letterSpacing: '0.15em',
            }}
            data-testid="panel-login-submit"
          >
            {busy ? 'Verificando…' : 'Ingresar'}
          </Button>
        </form>

        <p className="text-[10px] text-gray-600 mt-6 uppercase tracking-widest">
          Talengers · Todos los derechos reservados
        </p>
      </div>

      <style>{`
        @keyframes logo-float {
          0%,100% { transform: translateY(0px); }
          50%     { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
};

const ModuleTile = ({ mod, allowed }) => {
  const go = () => {
    if (allowed) window.location.href = mod.href;
  };
  const Icon = mod.Icon || ArrowRight;
  return (
    <button
      onClick={go}
      disabled={!allowed}
      data-testid={`panel-tile-${mod.key}`}
      title={mod.tooltip || mod.desc}
      className={`relative flex flex-col justify-between text-left p-6 rounded-2xl border transition-all group ${
        allowed
          ? 'border-gray-700 bg-gray-900 hover:border-gray-500 hover:-translate-y-0.5 cursor-pointer'
          : 'border-gray-800 bg-gray-950 opacity-50 cursor-not-allowed'
      }`}
      style={{ minHeight: 180 }}
    >
      {/* Custom hover tooltip — appears above the tile after ~250ms hover */}
      {allowed && mod.tooltip && (
        <div
          className="absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none z-20 w-64"
          data-testid={`panel-tile-tooltip-${mod.key}`}
        >
          <div className="bg-black border border-gray-700 rounded-lg px-3 py-2 shadow-2xl">
            <p className="text-[11px] leading-relaxed text-gray-200">{mod.tooltip}</p>
          </div>
          <div className="w-2 h-2 bg-black border-r border-b border-gray-700 transform rotate-45 mx-auto -mt-1"></div>
        </div>
      )}

      <div>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
          style={{ backgroundColor: mod.accent }}
        >
          {allowed ? (
            <Icon size={20} className="text-black" strokeWidth={2.2} />
          ) : (
            <LockIcon size={18} className="text-black" />
          )}
        </div>
        <h3
          className="text-2xl font-bold text-white mb-1"
          style={{ fontFamily: "'Bebas Neue', sans-serif" }}
        >
          {mod.label}
        </h3>
        <p className="text-sm text-gray-400">{mod.desc}</p>
      </div>
      <div className="mt-4 flex items-center justify-between text-xs">
        <span className="text-gray-500">Nivel {mod.minLevel}+</span>
        {allowed ? (
          <span className="text-gray-400 inline-flex items-center gap-1 group-hover:text-white">
            Abrir <ExternalLink size={12} />
          </span>
        ) : (
          <span className="text-gray-600">Sin acceso</span>
        )}
      </div>
    </button>
  );
};

const TYPE_STYLES = {
  talento: { label: 'Talento', bg: '#1B6B3A22', color: '#86efac', border: '#1B6B3A55' },
  propuesta: { label: 'Propuesta', bg: '#c4b5fd22', color: '#c4b5fd', border: '#c4b5fd55' },
  marca: { label: 'Marca', bg: '#F4A26122', color: '#fbbf24', border: '#F4A26155' },
};

const GlobalSearchBar = () => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/panel/search?q=${encodeURIComponent(q.trim())}`, cfg);
        setResults(res.data.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const grouped = results.reduce((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});
  const orderedTypes = ['talento', 'propuesta', 'marca'];

  return (
    <div ref={containerRef} className="relative mb-6" data-testid="panel-search">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        <input
          type="text"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Buscar talento, marca o propuesta..."
          className="w-full pl-10 pr-10 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 text-sm"
          data-testid="panel-search-input"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(''); setResults([]); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
            data-testid="panel-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute top-full left-0 right-0 mt-2 rounded-lg border border-gray-700 bg-gray-900 shadow-2xl z-30 max-h-96 overflow-y-auto"
          data-testid="panel-search-dropdown"
        >
          {loading && <div className="px-4 py-3 text-xs text-gray-500">Buscando...</div>}
          {!loading && results.length === 0 && (
            <div className="px-4 py-3 text-xs text-gray-500" data-testid="panel-search-empty">
              Sin resultados para "{q}"
            </div>
          )}
          {!loading && orderedTypes.map((t) => {
            const items = grouped[t];
            if (!items || items.length === 0) return null;
            const style = TYPE_STYLES[t];
            return (
              <div key={t} className="border-b border-gray-800 last:border-b-0">
                <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-950">
                  {style.label} · {items.length}
                </div>
                {items.map((r, idx) => (
                  <a
                    key={`${t}-${r.id || idx}`}
                    href={r.href}
                    className="flex items-center justify-between px-4 py-2 hover:bg-gray-800 transition-colors group"
                    data-testid={`panel-search-result-${t}-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span
                        className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}` }}
                      >
                        {style.label}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{r.label}</div>
                        {r.sub && <div className="text-[11px] text-gray-500 truncate">{r.sub}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      {r.meta && <span className="text-[11px] text-gray-400">{r.meta}</span>}
                      <ArrowRight size={12} className="text-gray-600 group-hover:text-white transition-colors" />
                    </div>
                  </a>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const HubView = ({ level, onLogout }) => {
  const [diag, setDiag] = useState(null); // null | 'checking' | 'ok' | 'ko'

  const runDiag = useCallback(async () => {
    setDiag('checking');
    try {
      await axios.get('/api/auth/me', cfg);
      setDiag('ok');
    } catch {
      setDiag('ko');
    }
  }, []);

  useEffect(() => { runDiag(); }, [runDiag]);

  return (
  <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }} data-testid="panel-hub">
    <div className="max-w-5xl mx-auto p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1
            className="text-4xl font-bold text-white"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            Panel Talengers
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Nivel de acceso: <span className="text-white font-semibold" data-testid="panel-level">{level}</span>
            {diag === 'ok' && (
              <span className="ml-3 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#1B6B3A', color: 'white' }} data-testid="panel-diag-ok">
                ● Sesión OK
              </span>
            )}
            {diag === 'ko' && (
              <span className="ml-3 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E63946', color: 'white' }} data-testid="panel-diag-ko" title="Un antivirus o extensión está bloqueando la cookie de sesión. Probá en otro navegador o modo incógnito.">
                ● Sesión bloqueada — cookies no persisten
              </span>
            )}
            {diag === 'checking' && (
              <span className="ml-3 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-300">
                ● Verificando…
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={onLogout}
          variant="outline"
          className="border-gray-700 text-gray-300 hover:bg-gray-800"
          data-testid="panel-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Salir
        </Button>
      </div>

      <GlobalSearchBar />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MODULES.filter((m) => level >= m.minLevel).map((m) => (
          <ModuleTile key={m.key} mod={m} allowed={true} />
        ))}
      </div>

      {diag === 'ko' && (
        <div className="mt-8 p-4 rounded-lg border border-red-700/60 bg-red-900/20 text-sm text-red-100" data-testid="panel-diag-warning">
          <p className="font-semibold mb-1">La sesión no se guardó en este navegador.</p>
          <p className="text-red-200">
            Un antivirus (Kaspersky/ESET/Bitdefender) o una extensión está removiendo la cookie de autenticación.
            Al clickear cualquier módulo te va a volver a pedir la contraseña. Intentá:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1 text-red-200">
            <li>Abrir <b>Modo Incógnito</b> (Ctrl+Shift+N en Chrome).</li>
            <li>Probar en otro navegador (Firefox / Edge).</li>
            <li>Desactivar temporalmente el antivirus / la protección web.</li>
            <li>Desactivar extensiones (ad-blockers, privacy tools).</li>
          </ul>
        </div>
      )}

      <p className="text-[11px] text-gray-600 text-center mt-8">
        Sesión válida por 30 min (o 7 días si marcaste "Recordarme").
      </p>
    </div>
  </div>
  );
};

const HubPanel = () => {
  const [level, setLevel] = useState(null); // null = loading, 0 = anon, 1..3 = authed
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/panel/me', cfg);
      setLevel(data.level || 0);
    } catch {
      setLevel(0);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleLogout = async () => {
    try {
      await axios.post('/api/panel/logout', null, cfg);
    } catch {
      /* ignore */
    }
    try {
      if (typeof window !== 'undefined') {
        if (window.localStorage) {
          window.localStorage.removeItem('talengers_bearer');
          window.localStorage.removeItem('talengers_panel_level');
        }
        if (window.sessionStorage) {
          window.sessionStorage.removeItem('talengers_bearer');
          window.sessionStorage.removeItem('talengers_panel_level');
        }
      }
    } catch { /* ignore */ }
    setLevel(0);
  };

  if (!ready) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#0a0a0a' }}
        data-testid="panel-loading"
      >
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-orange-400 border-t-transparent" />
      </div>
    );
  }

  if (!level) return <LoginView onAuthed={(lvl) => setLevel(lvl)} />;
  return <HubView level={level} onLogout={handleLogout} />;
};

export default HubPanel;
